import { IRRInputs } from '@/types/irr';

export interface LoanVsLeaseSavingResult {
  savingOnLoan: number;
  savingOnLoanPercent: number;
}

export function calculateLoanVsLeaseSaving(inputs: IRRInputs): LoanVsLeaseSavingResult {
  const assetCost = inputs.totalAssetCost;
  const baseCost = assetCost / (1 + inputs.gstRate / 100);
  const gstAmount = assetCost - baseCost;
  const securityDepositPercent = inputs.securityDepositPercent / 100;
  const taxRate = inputs.corporateTaxBenefit / 100;
  const roiLoan = (inputs.roiOnLoan ?? 7.5) / 100;
  const depRate = (inputs.depreciationPercent ?? 15) / 100;
  const pfPercent = inputs.processingFeesPercent / 100;
  const depositRefundPercent = inputs.refundSecurityDepositPercent / 100;
  const isQuarterly = inputs.paymentFrequency === 'quarterly';

  const gstPassbackEnabled = inputs.gstPassbackEnabled;
  const ptpmPostGstPassback = gstPassbackEnabled
    ? inputs.ptpmConsideration - inputs.gstPassback
    : inputs.ptpmConsideration;
  const finalPtpm = ptpmPostGstPassback;

  // Loan side - PMT calculation
  const downPayment = assetCost * securityDepositPercent;
  const financeAmount = assetCost - downPayment;

  const nPayments = isQuarterly ? inputs.tenureMonths / 3 : inputs.tenureMonths;
  const periodicRate = isQuarterly ? roiLoan / 4 : roiLoan / 12;

  const emi = financeAmount * periodicRate * Math.pow(1 + periodicRate, nPayments) / (Math.pow(1 + periodicRate, nPayments) - 1);
  const totalEmiPaid = emi * nPayments;
  const interestPaid = totalEmiPaid - financeAmount;

  // Depreciation
  let totalDepreciation = 0;
  let openingValue = baseCost;
  const numYears = Math.ceil(inputs.tenureMonths / 12);
  for (let y = 0; y < numYears; y++) {
    const dep = openingValue * depRate;
    totalDepreciation += dep;
    openingValue -= dep;
  }

  const totalTaxSaving = (interestPaid + totalDepreciation) * taxRate;
  const loanPf = -(financeAmount) * pfPercent;
  const loanInitialPayment = -downPayment;
  const loanNetCost = totalEmiPaid + Math.abs(loanPf) + Math.abs(loanInitialPayment) - gstAmount - totalTaxSaving;

  // Lease side
  const periodicRental = assetCost * finalPtpm / 1000;
  const totalRentals = periodicRental * nPayments;
  const leasePf = assetCost * pfPercent;
  const leaseInitialPayment = assetCost * securityDepositPercent;
  const leaseRefund = assetCost * depositRefundPercent;
  const leaseItClaim = totalRentals * taxRate;

  let leaseAdvanceRental = 0;
  if (inputs.advanceRentalPeriods > 0) {
    leaseAdvanceRental = periodicRental * inputs.advanceRentalPeriods;
  }

  const leaseNetCost = totalRentals + leaseAdvanceRental + leasePf + leaseInitialPayment - leaseRefund - leaseItClaim;
  const savingOnLoan = loanNetCost - leaseNetCost;
  const savingOnLoanPercent = savingOnLoan / assetCost;

  return { savingOnLoan, savingOnLoanPercent };
}
